import React, { useState, useRef, useCallback, useMemo, useEffect, useLayoutEffect } from "react";

import { Autocomplete, TextField, type AutocompleteProps } from "@mui/material";

import kutil from "@modules/kutil";
import workRoute from "@/modules/work_route";

import { usePopup } from "@components/popup/popup_context";

interface Props {
}

interface State {
  startPoses: string[];
  startZs: string[];
  startXs: string[];
  startYs: string[];
  endPoses: string[];
  endZs: string[];
  endXs: string[];
  endYs: string[];
}

const defState = {
  startPoses: [],
  startZs: [],
  startXs: [],
  startYs: [],
  endPoses:[],
  endZs: [],
  endXs: [],
  endYs: [],
};

interface Info {
  startPos: string;
  startZ: string;
  startX: string;
  startY: string;
  endPos: string;
  endZ: string;
  endX: string;
  endY: string;
}

const defInfo = {
  startPos: "",
  startZ: "",
  startX: "",
  startY: "",
  endPos: "",
  endZ: "",
  endX: "",
  endY: "",
};

export default function WorkOrder(_props: Props) {
  const { setOption, setActions, onClose } = usePopup();
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  const workOrderRef = useRef(workOrder);
  workOrderRef.current = workOrder;

  // 팝업 설정
  useLayoutEffect(() => {
    setOption({
      title: "작업지시",
      maxWidth: "700px"
    });
    setActions([
      {
        id: "closePopup",
        label: "취소",
        className: "basic outline",
        onClick: () => onClose(),
      },
      {
        id: "order",
        label: "지시",
        className: "basic fill",
        onClick: () => workOrderRef.current(),
      }
    ]);
  }, []);

  // 최초실행시 출발위치, 목적위치 초기화
  useEffect(() => {
    setState(prev => ({...prev,
      startPoses: workRoute.validStarts(info.endPos),
      endPoses: workRoute.validEnds(info.startPos),
    }));
  }, []);

  // 출발위치가 변경된 경우
  useEffect(() => {
    setState(prev => ({
      ...prev,
      startZs: [],
      startXs: [],
      startYs: [],
    }));
    setInfo(prev => ({
      ...prev,
      startZ: "",
      startX: "",
      startY: "",
    }));
    const _pos = kutil.toInt(info.startPos, 0);
    if (_pos > 0 && _pos < 10) {
      setState(prev => ({
        ...prev,
        startZs: kutil.range(1, 2).map(n => "" + n),
        startXs: _pos <= 5 ? kutil.range(1, 45).map(n => "" + n) : kutil.range(1, 18).map(n => "" + n),
        startYs: _pos <= 5 ? kutil.range(1, 76).map(n => "" + n) : kutil.range(1, 15).map(n => "" + n),
      }));
    }
    const validEndPoses = workRoute.validEnds(info.startPos);
    setState(prev => ({
      ...prev,
      endPoses: validEndPoses,
    }));
    if (!validEndPoses.find(p => p == info.endPos)) {
      setInfo(prev => ({
        ...prev,
        endPos: "",
      }));
    }
  }, [info.startPos]);

  // 목적위치가 변경된 경우
  useEffect(() => {
    setState(prev => ({
      ...prev,
      endZs: [],
      endXs: [],
      endYs: [],
    }));
    setInfo(prev => ({
      ...prev,
      endZ: "",
      endX: "",
      endY: "",
    }));
    const _pos = kutil.toInt(info.endPos, 0);
    if (_pos > 0 && _pos < 10) {
      setState(prev => ({
        ...prev,
        endZs: kutil.range(1, 2).map(n => "" + n),
        endXs: _pos <= 5 ? kutil.range(1, 45).map(n => "" + n) : kutil.range(1, 18).map(n => "" + n),
        endYs: _pos <= 5 ? kutil.range(1, 76).map(n => "" + n) : kutil.range(1, 15).map(n => "" + n),
      }));
    }
    const validStartPoses = workRoute.validStarts(info.endPos);
    setState(prev => ({
      ...prev,
      startPoses: validStartPoses,
    }));
    if (!validStartPoses.find(p => p == info.startPos)) {
      setInfo(prev => ({
        ...prev,
        startPos: "",
      }));
    }
  }, [info.endPos]);

  async function workOrder() {
    if ((!info.startPos)
      || (state.startZs.length > 0 && !info.startZ)
      || (state.startXs.length > 0 && !info.startX)
      || (state.startYs.length > 0 && !info.startY)
    ) {
      alert("출발위치를 선택하세요.");
      return;
    }
    if (!info.endPos
      || (state.endZs.length > 0 && !info.endZ)
      || (state.endXs.length > 0 && !info.endX)
      || (state.endYs.length > 0 && !info.endY)
    ) {
      alert("도착위치를 선택하세요.");
      return;
    }

    if (await kutil.confirm("작업지시를 하시겠습니까?") == false) {
      return false;
    }
    try {
      const reqInfo = {
        startPos: workRoute.getPos(info.startPos, info.startZ, info.startX, info.startY),
        endPos: workRoute.getPos(info.endPos, info.endZ, info.endX, info.endY),
      }
      const data = await kutil.sendPost("/api/work/work_order.proc?mode=add", reqInfo, {
      });

      await kutil.alert("작업이 등록되었습니다.");
      onClose();
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      <div className="popup-contents">
        <div className="table-wrap">
          <table>
            <colgroup>
              <col style={{ width: "33.3%" }} />
              <col style={{ width: "66.6%" }} />
            </colgroup>
            <tbody>
              <tr>
                <th scope="row" className="bd-left">출발위치</th>
                <td>
                  <div className="select-location">
                    <CustomAutocomplete
                      options={state.startPoses}
                      value={info.startPos}
                      onChange={(v) => setInfo(prev => ({...prev, startPos: v}))}
                    />
                    { info.startPos && state.startZs.length > 0 &&
                      <CustomAutocomplete
                        options={state.startZs}
                        value={info.startZ}
                        onChange={(v) => setInfo(prev => ({...prev, startZ: v}))}
                      />
                    }
                    { info.startPos && state.startXs.length > 0 &&
                      <CustomAutocomplete
                        options={state.startXs}
                        value={info.startX}
                        onChange={(v) => setInfo(prev => ({...prev, startX: v}))}
                      />
                    }
                    { info.startPos && state.startYs.length > 0 &&
                      <CustomAutocomplete
                        options={state.startYs}
                        value={info.startY}
                        onChange={(v) => setInfo(prev => ({...prev, startY: v}))}
                      />
                    }
                  </div>
                </td>
              </tr>
              <tr>
                <th scope="row" className="bd-left">도착위치</th>
                <td>
                  <div className="select-location">
                    <CustomAutocomplete
                      options={state.endPoses}
                      value={info.endPos}
                      onChange={(v) => setInfo(prev => ({...prev, endPos: v}))}
                    />
                    { info.endPos && state.endZs.length > 0 &&
                      <CustomAutocomplete
                        options={state.endZs}
                        value={info.endZ}
                        onChange={(v) => setInfo(prev => ({...prev, endZ: v}))}
                      />
                    }
                    { info.endPos && state.endXs.length > 0 &&
                      <CustomAutocomplete
                        options={state.endXs}
                        value={info.endX}
                        onChange={(v) => setInfo(prev => ({...prev, endX: v}))}
                      />
                    }
                    { info.endPos && state.endYs.length > 0 &&
                      <CustomAutocomplete
                        options={state.endYs}
                        value={info.endY}
                        onChange={(v) => setInfo(prev => ({...prev, endY: v}))}
                      />
                    }
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

interface AutocompleteOption {
  code: string;
  value: string;
};

interface CustomAutocompleteProps extends Omit<AutocompleteProps<AutocompleteOption, false, false, false>, "value" | "onChange" | "renderInput" | "options"> {
  options: string[];
  value: string;
  onChange: (code: string) => void;
  label?: string;
}

const CustomAutocomplete = React.memo(
  ({ options, value, onChange, label, ...rest }: CustomAutocompleteProps) => {
    const customOptions: AutocompleteOption[] = useMemo(() => {
      return options.map(v => ({
        code: v,
        value: v
      }));
    }, [options]);

    const selected = customOptions.find((o) => o.code === value) ?? null;
    useEffect(() => {
      if (customOptions.length === 0) return;
      if (value && !selected) onChange("");
    }, [value, selected, onChange]);

    return (
      <Autocomplete<AutocompleteOption, false, false, false>
        {...rest}
        forcePopupIcon={false}
        options={customOptions}
        value={selected}
        onChange={(_, newValue) => onChange(newValue?.code ?? "")}
        onBlur={(e) => {
          const text = (e.target as HTMLInputElement).value;
          const code = customOptions.find((o) => o.code === text || o.value === text)?.code ?? "";
          onChange(code);
        }}
        getOptionLabel={(option) => option.value}
        isOptionEqualToValue={(option, val) => option.code === val.code}
        sx={[
          {
            "& .MuiAutocomplete-clearIndicator": { display: "none" },
            "&.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot": { paddingRight: 0 },
          },
          ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx]),
        ]}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="standard"
            sx={{
              "& .MuiAutocomplete-input": { paddingLeft: "8px !important" },
              // standard variant 기본 밑줄 제거 (평상시 / hover / focus)
              "& .MuiInput-underline:before, & .MuiInput-underline:after": {
                display: "none",
              },
            }}
          />
        )}
        slotProps={{ listbox: { sx: { "& .MuiAutocomplete-option": { paddingLeft: "16px !important" } } } }}
      />
    );
  }
);

CustomAutocomplete.displayName = "CustomAutocomplete";
